import React, { useEffect, useMemo, useState } from "react";
import { AppModalFooter, AppModalShell } from "./app-ui.js";
import { Button } from "./legacy-ui.js";

const formatToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const ALL_PRODUCTS = "";

export default function NotifyDayDialog({
  open,
  title = "Notificar administradores",
  hint = "Selecciona el día que quieres enviar.",
  days = [],
  confirming = false,
  productPicker = false,
  onClose,
  onConfirm,
}) {
  const today = formatToday();
  const preferred =
    days.find((day) => day.enabled && day.date === today)?.date ||
    days.find((day) => day.enabled)?.date ||
    "";
  const [selected, setSelected] = useState(preferred);
  const [selectedProduct, setSelectedProduct] = useState(ALL_PRODUCTS);

  const selectedDay = useMemo(
    () => days.find((day) => day.date === selected) || null,
    [days, selected],
  );
  const productOptions = useMemo(
    () => selectedDay?.products || [],
    [selectedDay],
  );

  useEffect(() => {
    if (!open) return;
    const stillValid = days.some((day) => day.enabled && day.date === selected);
    if (!stillValid) setSelected(preferred);
  }, [open, days, preferred, selected]);

  useEffect(() => {
    if (!open || !productPicker) return;
    const stillValid =
      selectedProduct === ALL_PRODUCTS ||
      productOptions.some((p) => p.enabled && String(p.id) === String(selectedProduct));
    if (!stillValid) setSelectedProduct(ALL_PRODUCTS);
  }, [open, productPicker, productOptions, selectedProduct]);

  if (!open) return null;

  const dayReady = Boolean(selected) && days.some((day) => day.enabled && day.date === selected);
  const productReady =
    !productPicker ||
    selectedProduct === ALL_PRODUCTS ||
    productOptions.some((p) => p.enabled && String(p.id) === String(selectedProduct));
  const canSend = dayReady && productReady;

  return (
    <AppModalShell
      open={open}
      onClose={onClose}
      title={title}
      widthClass="max-w-lg"
      fitContent
      footer={
        <AppModalFooter>
          <Button variant="secondary" onClick={onClose} disabled={confirming}>
            Cancelar
          </Button>
          <Button
            onClick={() =>
              onConfirm(selected, productPicker ? selectedProduct || undefined : undefined)
            }
            disabled={confirming || !canSend}
          >
            {confirming ? "Notificando…" : "Enviar notificación"}
          </Button>
        </AppModalFooter>
      }
    >
      <p className="text-sm text-gray-600 mb-3">{hint}</p>
      {days.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">No hay días en esta semana.</p>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Día</p>
            {days.map((day) => {
              const active = selected === day.date;
              return (
                <button
                  key={day.date}
                  type="button"
                  disabled={!day.enabled}
                  onClick={() => {
                    setSelected(day.date);
                    setSelectedProduct(ALL_PRODUCTS);
                  }}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
                    !day.enabled
                      ? "border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed"
                      : active
                        ? "border-indigo-500 bg-indigo-50 text-indigo-900"
                        : "border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-sm">{day.label}</span>
                    <span className="text-xs text-gray-500">
                      {day.meta || (day.enabled ? "Listo" : "Sin carga")}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {productPicker && dayReady ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Tipo de producto
              </p>
              <button
                type="button"
                onClick={() => setSelectedProduct(ALL_PRODUCTS)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
                  selectedProduct === ALL_PRODUCTS
                    ? "border-indigo-500 bg-indigo-50 text-indigo-900"
                    : "border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-sm">Todos los productos</span>
                  <span className="text-xs text-gray-500">{selectedDay?.meta || "Resumen del día"}</span>
                </div>
              </button>
              {productOptions.map((product) => {
                const active = String(selectedProduct) === String(product.id);
                return (
                  <button
                    key={product.id}
                    type="button"
                    disabled={!product.enabled}
                    onClick={() => setSelectedProduct(String(product.id))}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
                      !product.enabled
                        ? "border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed"
                        : active
                          ? "border-indigo-500 bg-indigo-50 text-indigo-900"
                          : "border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-sm">{product.name}</span>
                      <span className="text-xs text-gray-500">
                        {product.meta || (product.enabled ? "Listo" : "Sin promedio")}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      )}
    </AppModalShell>
  );
}
